import React, { useState, useEffect, useCallback, useContext } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Spin, Select, Switch, Tooltip } from 'antd';
import { ImageView } from '../styles';
import { correctBaseUrl } from '../../../utils/svgUtils';
import { useDocumentContext, COMPLEX_DOCUMENT_CONTEXT_KEYS } from '../../../context/complexDocumentContext';
import { useArtboardContext, CONTEXT_KEYS } from '../../../context/complexArtboardContext';
import { PageElementsContext } from '../../../context/pageElementsContext';
import { IMAGE_TOOLBAR } from '../translationValues';
import { getAllImages } from '../../../utils/services/imageAPI';

const { Option } = Select;

const NAMESPACES = ['IMAGE_TOOLBAR'];

const ImageEditor = (props) => {
  const { element, dataSetKey, dataIndex, styles = {}, selected } = props;
  const { t } = useTranslation(NAMESPACES);

  const [isVariable, toggleVariable] = useState(element.style.isVariable || false);
  const { updateComplexElementDetails } = useContext(PageElementsContext);
  
  const customSelector = useCallback(store => store[COMPLEX_DOCUMENT_CONTEXT_KEYS.GLOBAL_VARIABLES], []);
  const [globalVariables] = useDocumentContext(customSelector, `ImageEditor-${element.uuid}`);
  
  const previewModeSelector = useCallback(store => store[CONTEXT_KEYS.ENABLE_PAGE_OVERFLOW], []);
  const [pageOverflowEnabled] = useArtboardContext(previewModeSelector,  `ImageEditor-${element.uuid}`);
  
  const dataSetSelector = useCallback(store => store[COMPLEX_DOCUMENT_CONTEXT_KEYS.DATA_SET], []);
  const [dataSet] = useDocumentContext(dataSetSelector, `ImageEditor-${element.uuid}`);

  const noVariablesDesignSelector = useCallback(store => store[CONTEXT_KEYS.IS_INTERACTIVE_DESIGN] || store[CONTEXT_KEYS.IS_OPEN_DESIGN], []);
  const [disableVariables] = useArtboardContext(noVariablesDesignSelector, `ImageEditor`);
  
  const [resolvedImageSrc, setResolvedImageSrc] = useState();
  
  const fetchAllImages = useCallback(async () => {
    let params = {
    }
    let response = await getAllImages(params);
    if (response && response.statusCode === 201 && Array.isArray(response.data)) {
      const matchedImage = response.data.find(obj => obj.imageAddress === element.url);
      if (matchedImage?.imageLink) {
        setResolvedImageSrc(matchedImage.imageLink);
      }
    }
  }, [element.url]);

  useEffect(() => {
    toggleVariable(isDynamic => {
      if (isDynamic !== element.style.isVariable) {
        return element.style.isVariable;
      }
      return isDynamic;
    });
    setResolvedImageSrc(undefined);
    if (element.url && element.url.includes("IMAGE_")) {
      fetchAllImages();
    }
  }, [element, fetchAllImages])

  const onSelect = ({key}) => {
    updateComplexElementDetails(element.uuid, {
      style: { isVariable: true },
      content: `{${key}}`
    }, 'image');
  }

  const onDynamicSwitch = useCallback((isDynamic) => {
    if (element.content !== undefined) {
      updateComplexElementDetails(element.uuid, {
        style: { isVariable: isDynamic },
        content: isDynamic ? element.content: undefined
      }, 'image');
    }
    toggleVariable(isDynamic);
  }, [element]);

  const onImageError = useCallback((e) => {
    // TODO : Set fallback image
    e.target.src = element.url;
  }, [element]);

  if (!element.url) {
    return <Spin />;
  }

  let content = element.content;
  let variableName;
  if (element.style.isVariable) {
    content = Number(element.content?.replaceAll('{', '')?.replaceAll('}', ''));
    variableName = globalVariables.find(gVar => gVar.id === content)?.placeHolderText;
  }

  if (pageOverflowEnabled && element.style.isVariable && content !== undefined) {
    const imageLink = dataSetKey ? dataSet[dataSetKey][dataIndex][variableName] : dataSet[variableName];
    return (
      <ImageView
        id={`img-${element.uuid}`}
        src={imageLink}
        alt="droppable-image"
        style={{ ...styles, objectFit: 'contain' }}
        onError={onImageError}
      />
    );
  }

  return (
    <>
      <ImageView
        id={`img-${element.uuid}`}
        src={resolvedImageSrc || correctBaseUrl(element.url)}
        alt="droppable-image"
        style={styles}
      />

      {selected && ReactDOM.createPortal(
        (
          <div style={{ padding: '4px 0' }}>
            <Tooltip
              placement="bottom"
              title={t(IMAGE_TOOLBAR.CHANGE_TO_DYNAMIC_IMAGE.key, IMAGE_TOOLBAR.CHANGE_TO_DYNAMIC_IMAGE.value)}
            >
              <Switch style={{ marginRight: 10 }} checked={isVariable} onChange={onDynamicSwitch} disabled={disableVariables} />
            </Tooltip>
            {!isVariable && <div style={{ color: 'white', display: 'inline-block', marginRight: 10 }}>
              {t(IMAGE_TOOLBAR.CHANGE_TO_DYNAMIC_IMAGE_LABEL.key, IMAGE_TOOLBAR.CHANGE_TO_DYNAMIC_IMAGE_LABEL.value)}
            </div>}
            {isVariable && <Select
              showSearch
              style={{ width: 150, marginRight: 10 }}
              placeholder={'Select a variable'}
              optionFilterProp="children"
              value={variableName ? { key: content, label: variableName } : undefined}
              onSelect={onSelect}
              labelInValue
              filterOption={(input, option) =>
                option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
            >
              {globalVariables.filter(data => data.type !== "ListString" && data.type !== 'Date').map((data, index) => (
                <Option key={data.id + index} value={data.id}>
                  {data.placeHolderText}
                </Option>
              ))}
            </Select>}
          </div>
        ),
        document.getElementById('document-editor-toolbar'),
      )}
    </>
  )
}

export default ImageEditor;